import { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FaUserClock } from "react-icons/fa";
import { debounce } from "lodash";
import { enqueueSnackbar } from "notistack";

import { fetchPrograms } from "../../../redux/program/programActions";
import { fetchSubjects } from "../../../redux/subject/subjectActions";

import Loader from "../../../components/Loader/Loader";
import Table from "../../../components/Table/Table";
import Pagination from "../../../components/Pagination/Pagination";
import Modal from "../../../components/Modal/Modal";
import Input from "../../../components/Input/Input";

import AttendanceTeacherMapForm from "./AttendanceTeacherMapForm";
import { tableConstants } from "./AttendanceMapTableConst";

import {
  getMappedPrograms,
  createAttendanceTeacherMapping,
} from "../../../services/AppManagement/AttendanceService";
import { getUsers } from "../../../services/UserService";

const AttendanceTeacherMap = () => {
  const dispatch = useDispatch();

  const { programData } = useSelector((state) => ({
    programData: state.program.programs?.data ?? [],
  }));

  /* -------------------- State -------------------- */
  const [searchName, setSearchName] = useState("");
  const [permissions] = useState(
    JSON.parse(localStorage.getItem("permissions"))
  );

  const [teacherData, setTeacherData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPage, setTotalPage] = useState(0);
  const [loader, setLoader] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [teacherId, setTeacherId] = useState(null);
  const [loading, setLoading] = useState(false);

  const [programIds, setProgramIds] = useState([]);
  const [leaveApprovalProgramIds, setLeaveApprovalProgramIds] = useState([]);
  const [homeworkProgramIds, setHomeworkProgramIds] = useState([]);

  /* -------------------- Helpers -------------------- */
  const allProgramIds = useMemo(
    () => programData.map((p) => p.id.toString()),
    [programData]
  );

  const toggleCheckbox = (value, checked, setter, state) => {
    setter(checked ? [...state, value] : state.filter((id) => id !== value));
  };

  const toggleSelectAll = (checked, setter) => {
    setter(checked ? allProgramIds : []);
  };

  /* -------------------- API Calls -------------------- */
  const fetchUserList = async (name, page) => {
    try {
      setLoader(true);
      setCurrentPage(page);
      const response = await getUsers({
        name,
        page,
        includeAttendanceMapping: true,
      });

      if (response.success) {
        setTeacherData(response.data);
        setTotalPage(response.last_page);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoader(false);
    }
  };

  const fetchMappedProgramsByTeacher = async (id) => {
    try {
      setLoading(true);
      setProgramIds([]);
      setLeaveApprovalProgramIds([]);
      setHomeworkProgramIds([]);

      const response = await getMappedPrograms(id);
      if (response.status === 200) {
        const data = response.data.data;
        setProgramIds(data.attendances.map((i) => i.program_id.toString()));
        setLeaveApprovalProgramIds(
          data.leave_approver.map((i) => i.program_id.toString())
        );
        setHomeworkProgramIds(
          data.teacher_mapping_homework.map((i) => i.program_id.toString())
        );
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  /* -------------------- Handlers -------------------- */
  const openModal = (row) => {
    setTeacherId(row.id);
    fetchMappedProgramsByTeacher(row.id);
    setIsModalOpen(true);
  };

  const submitHandler = async (e) => {
    e.preventDefault();

    const payload = {
      program_ids: programIds,
      session_id: localStorage.getItem("session_id"),
      teacher_id: teacherId,
      leave_approval_program_ids: leaveApprovalProgramIds,
      homework_program_ids: homeworkProgramIds,
    };

    try {
      const response = await createAttendanceTeacherMapping(payload);
      if ([200, 201].includes(response.status)) {
        enqueueSnackbar(response.data.message.success, {
          variant: "success",
        });
        setIsModalOpen(false);
        fetchUserList(searchName, currentPage);
      }
    } catch {
      enqueueSnackbar("Something went wrong", { variant: "error" });
    }
  };

  /* -------------------- Search -------------------- */
  const debouncedSearch = useMemo(
    () => debounce((value) => fetchUserList(value, 1), 300),
    []
  );

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchName(value);
    if (value.length > 2 || value.length === 0) {
      debouncedSearch(value);
    }
  };

  /* -------------------- Effects -------------------- */
  useEffect(() => {
    if (!localStorage.getItem("organization_id")) {
      enqueueSnackbar("Select any organization!", { variant: "error" });
      return;
    }

    fetchUserList("", 1);
    dispatch(fetchPrograms({ selected: 0 }));
    dispatch(fetchSubjects({ selected: 0 }));

    return () => debouncedSearch.cancel();
  }, [debouncedSearch, dispatch]);

  /* -------------------- Render -------------------- */
  if (loader) return <Loader />;

  return (
    <div className="card">
      <div className="card-header">
        <div className="main-title-page d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center gap-2">
            <FaUserClock />
            <span>Attendance Teacher Mapping</span>
          </div>
          <div style={{ width: 280 }}>
            <Input
              type="text"
              placeholder="Search by name..."
              value={searchName}
              onChangeHandler={handleSearch}
              autoFocus
            />
          </div>
        </div>
      </div>

      <div className="card-body">
        <Modal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          size="xl"
        >
          <AttendanceTeacherMapForm
            submitHandler={submitHandler}
            closeModal={() => setIsModalOpen(false)}
            programData={programData}
            loader={loading}
            program_ids={programIds}
            leave_approval_program_ids={leaveApprovalProgramIds}
            homework_program_ids={homeworkProgramIds}
            onInputChange={(e) =>
              toggleCheckbox(
                e.target.value,
                e.target.checked,
                setProgramIds,
                programIds
              )
            }
            onSwitchChange={(e) =>
              toggleSelectAll(e.target.checked, setProgramIds)
            }
            onApproverProgramHandler={(e) =>
              toggleCheckbox(
                e.target.value,
                e.target.checked,
                setLeaveApprovalProgramIds,
                leaveApprovalProgramIds
              )
            }
            onSelectAllApproverProgramHandler={(e) =>
              toggleSelectAll(e.target.checked, setLeaveApprovalProgramIds)
            }
            onHomeworkInputChange={(e) =>
              toggleCheckbox(
                e.target.value,
                e.target.checked,
                setHomeworkProgramIds,
                homeworkProgramIds
              )
            }
            onHomeworkSwitchChange={(e) =>
              toggleSelectAll(e.target.checked, setHomeworkProgramIds)
            }
          />
        </Modal>

        {teacherData.length > 0 && (
          <>
            <Table
              cols={tableConstants(permissions, openModal, currentPage)}
              data={teacherData}
              striped
              hoverable
            />
            <Pagination
              totalPage={totalPage}
              currentPage={currentPage - 1}
              handlePageChange={(i) =>
                fetchUserList(searchName, i.selected + 1)
              }
            />
          </>
        )}
      </div>
    </div>
  );
};

export default AttendanceTeacherMap;
